import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import StatCard from "@/components/base/StatCard";
import { Input, Select } from "@/components/base/Field";
import { EmptyState, ErrorState, LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import ReasonModal from "@/components/feature/ReasonModal";
import { useAuth } from "@/contexts/AuthContext";
import { useAsync } from "@/hooks/useAsync";
import { getCashDay, reopenCash, type CashierDay } from "@/services/closingService";
import { PAYMENT_METHOD_LABEL } from "@/services/paymentService";
import { getSchoolInfo } from "@/services/schoolService";
import type { PaymentMethod } from "@/types";
import { exportCashClosingPdf } from "@/utils/cashClosingPdf";
import { formatMoney, plural } from "@/utils/format";
import CountModal from "./components/CountModal";

const METHODS = Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[];
const todayISO = () => new Date().toISOString().slice(0, 10);
const shiftDay = (date: string, days: number) => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const time = (iso: string) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

/** Caisse du jour : encaissements par caissier et par mode, comptage, validation et fiche imprimable. */
export default function CashPage() {
  const { notify } = useToast();
  const { can, role } = useAuth();
  const isAdmin = role?.isSystem === true;
  const canValidate = can("cash:validate");

  const [date, setDate] = useState(todayISO());
  const [cashierFilter, setCashierFilter] = useState("all");
  const [counting, setCounting] = useState<CashierDay | null>(null);
  const [reopening, setReopening] = useState<CashierDay | null>(null);
  const { data, loading, error, reload } = useAsync(() => getCashDay(date), [date]);

  const operations = useMemo(
    () => (data?.operations ?? []).filter((o) => cashierFilter === "all" || o.cashierKey === cashierFilter),
    [data, cashierFilter],
  );

  if (loading && !data) return <LoadingState label="Chargement de la caisse..." />;
  if (error || !data) return <ErrorState message={error ?? "Chargement impossible."} onRetry={reload} />;

  const validated = data.cashiers.filter((c) => c.closing).length;
  const gaps = data.cashiers.reduce((acc, c) => acc + (c.closing?.gap ?? 0), 0);

  const print = async (cashier: CashierDay) => {
    try {
      await exportCashClosingPdf(
        cashier,
        data.operations.filter((o) => o.cashierKey === cashier.cashierKey),
        date,
        await getSchoolInfo(),
      );
    } catch (err) {
      notify(err instanceof Error ? err.message : "Impression impossible.", "error");
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Caisse du jour"
        subtitle="Encaissements par caissier et par mode, comptage et validation"
        icon="ri-safe-2-line"
        actions={
          <div className="flex items-end gap-2">
            <Button variant="outline" icon="ri-arrow-left-s-line" onClick={() => setDate(shiftDay(date, -1))} aria-label="Jour précédent" />
            <Input type="date" value={date} max={todayISO()} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Jour" />
            <Button
              variant="outline"
              icon="ri-arrow-right-s-line"
              onClick={() => setDate(shiftDay(date, 1))}
              disabled={date >= todayISO()}
              aria-label="Jour suivant"
            />
            {date !== todayISO() && (
              <Button variant="ghost" onClick={() => setDate(todayISO())}>
                Aujourd'hui
              </Button>
            )}
          </div>
        }
      />

      {data.monthClosed && (
        <p className="mb-4 flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2.5 text-sm text-secondary-800">
          <i className="ri-lock-2-line"></i> Ce mois est clôturé : la caisse est en lecture seule.
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Total encaissé" value={formatMoney(data.total)} icon="ri-money-dollar-circle-line" tone="primary" />
        <StatCard
          label="Caisses validées"
          value={`${validated} / ${data.cashiers.length}`}
          icon="ri-checkbox-circle-line"
          tone="secondary"
        />
        <StatCard
          label="Écarts constatés"
          value={validated ? formatMoney(gaps) : "—"}
          icon="ri-scales-3-line"
          tone="accent"
          hint={validated ? "Compté − attendu, caisses validées" : "Aucune caisse validée"}
        />
      </div>

      <Card className="mt-5">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-foreground-400">Par mode d'encaissement</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {METHODS.map((m) => (
            <div key={m} className="rounded-md border border-background-200 bg-background-100/50 px-3 py-2.5">
              <p className="text-xs uppercase tracking-wide text-foreground-400">{PAYMENT_METHOD_LABEL[m]}</p>
              <p className="text-base font-semibold text-foreground-900">{formatMoney(data.totals[m] ?? 0)}</p>
            </div>
          ))}
        </div>
      </Card>

      {data.cashiers.length === 0 ? (
        <Card className="mt-5">
          <EmptyState icon="ri-safe-2-line" title="Aucun encaissement ce jour" message="Ni reçu ni recette enregistrés pour cette date." />
        </Card>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {data.cashiers.map((c) => (
            <Card key={c.cashierKey}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{c.cashierName}</p>
                  <p className="text-xs text-foreground-500">
                    {plural(c.operations, "opération")}{c.cancelled ? ` · ${plural(c.cancelled, "annulée")}` : ""}
                  </p>
                </div>
                {c.closing ? (
                  <Badge tone={c.closing.gap === 0 ? "success" : "danger"} icon="ri-lock-2-line">
                    {c.closing.gap === 0 ? "Validée, caisse juste" : `Validée, écart ${formatMoney(c.closing.gap)}`}
                  </Badge>
                ) : (
                  <Badge tone="secondary" icon="ri-time-line">
                    À compter
                  </Badge>
                )}
              </div>

              <table className="mt-3 w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-foreground-400">
                  <tr>
                    <th className="py-1 font-medium">Mode</th>
                    <th className="py-1 font-medium text-right">Attendu</th>
                    {c.closing && <th className="py-1 font-medium text-right">Compté</th>}
                  </tr>
                </thead>
                <tbody>
                  {METHODS.filter((m) => (c.expected[m] ?? 0) > 0 || (c.closing?.counted[m] ?? 0) > 0).map((m) => (
                    <tr key={m} className="border-t border-background-200">
                      <td className="py-1.5 text-foreground-700">{PAYMENT_METHOD_LABEL[m]}</td>
                      <td className="py-1.5 text-right">{formatMoney(c.expected[m] ?? 0)}</td>
                      {c.closing && <td className="py-1.5 text-right">{formatMoney(c.closing.counted[m] ?? 0)}</td>}
                    </tr>
                  ))}
                  <tr className="border-t border-background-300 font-semibold">
                    <td className="py-1.5">Total</td>
                    <td className="py-1.5 text-right">{formatMoney(c.total)}</td>
                    {c.closing && <td className="py-1.5 text-right">{formatMoney(c.total + c.closing.gap)}</td>}
                  </tr>
                </tbody>
              </table>
              {c.closing && (
                <p className="mt-2 text-xs text-foreground-500">
                  Validée par {c.closing.validatedByName}
                  {c.closing.comment ? ` — « ${c.closing.comment} »` : ""}
                </p>
              )}

              <div className="mt-3 flex flex-wrap justify-end gap-2">
                {!c.closing && canValidate && !data.monthClosed && date <= todayISO() && (
                  <Button size="sm" icon="ri-calculator-line" onClick={() => setCounting(c)}>
                    Compter et valider
                  </Button>
                )}
                <Button size="sm" variant="outline" icon="ri-printer-line" onClick={() => void print(c)}>
                  {c.closing ? "Fiche de clôture" : "État provisoire"}
                </Button>
                {c.closing && isAdmin && !data.monthClosed && (
                  <Button size="sm" variant="danger" icon="ri-lock-unlock-line" onClick={() => setReopening(c)}>
                    Rouvrir
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {data.operations.length > 0 && (
        <Card className="mt-5" padded={false}>
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-background-200 p-4">
            <p className="text-sm font-semibold text-foreground-900">Opérations du jour</p>
            <Select value={cashierFilter} onChange={(e) => setCashierFilter(e.target.value)} wrapperClass="w-56" aria-label="Caissier">
              <option value="all">Tous les caissiers</option>
              {data.cashiers.map((c) => (
                <option key={c.cashierKey} value={c.cashierKey}>
                  {c.cashierName}
                </option>
              ))}
            </Select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Heure</th>
                  <th className="px-3 py-2.5 font-medium">Référence</th>
                  <th className="px-3 py-2.5 font-medium">Libellé</th>
                  <th className="px-3 py-2.5 font-medium">Caissier</th>
                  <th className="px-3 py-2.5 font-medium">Mode</th>
                  <th className="px-4 py-2.5 font-medium text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {operations.map((o) => (
                  <tr key={`${o.kind}-${o.id}`} className={`border-t border-background-200 ${o.cancelled ? "text-foreground-400" : ""}`}>
                    <td className="px-4 py-2.5">{time(o.createdAt)}</td>
                    <td className="px-3 py-2.5">
                      <span className="font-medium">{o.reference}</span>
                      <span className="ml-2 text-xs text-foreground-400">{o.kind === "RECU" ? "Reçu" : "Recette"}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      {o.label}
                      {o.cancelled && (
                        <Badge tone="danger" className="ml-2">
                          Annulé
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2.5">{o.cashierName}</td>
                    <td className="px-3 py-2.5">{PAYMENT_METHOD_LABEL[o.method]}</td>
                    <td className={`px-4 py-2.5 text-right font-medium ${o.cancelled ? "line-through" : ""}`}>{formatMoney(o.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <CountModal cashier={counting} date={date} onClose={() => setCounting(null)} onValidated={reload} />
      <ReasonModal
        open={reopening !== null}
        title="Rouvrir la caisse"
        subtitle={reopening ? `${reopening.cashierName} — ${date.split("-").reverse().join("/")}` : undefined}
        message="La journée de ce caissier redeviendra modifiable : il faudra la recompter et la valider à nouveau."
        confirmLabel="Rouvrir la caisse"
        onClose={() => setReopening(null)}
        onConfirm={async (reason) => {
          await reopenCash(reopening!.closing!.id, reason);
          notify("Caisse rouverte.");
          reload();
        }}
      />
    </div>
  );
}
